import React, { useMemo, useState } from "react";
import Swal from "sweetalert2";
import { Form, Modal, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faCircleCheck, faMagnifyingGlass, faRotateLeft } from "@fortawesome/free-solid-svg-icons";
import { BAJA_MOTIVOS, PROVINCIAL_CARDS, identifyDeviceModel } from "./bajaTerminalCatalog";

const todayForInput = () => new Date().toISOString().slice(0, 10);
const normalizeDigits = (value) => String(value || "").replace(/\D/g, "");
const read = (item, ...keys) => keys.map((key) => item?.[key]).find((value) => value !== undefined && value !== null) ?? "";
const asDateForApi = (value) => String(value || "").replace(/-/g, "/");

const initialState = () => ({
  currentStep: 1,
  terminalInput: "",
  terminalDetails: null,
  identityConfirmed: false,
  hasProvincialCards: "",
  selectedProvincialCards: {},
  verifiedProvincialCards: {},
  provincialReviewConfirmed: false,
  motivo: "BAJA",
  fechaBaja: todayForInput(),
  observaciones: "",
  error: "",
});

function Stepper({ currentStep }) {
  const steps = [
    ["Terminal", "Ingresar numero"],
    ["Identificacion", "Validar datos"],
    ["Tarjetas", "Revisar bajas"],
    ["Confirmacion", "Cargar baja"],
  ];

  return (
    <div className="crm-baja-terminal__stepper" aria-label="Progreso de baja por terminal">
      {steps.map(([title, subtitle], index) => {
        const number = index + 1;
        const completed = currentStep > number;
        const active = currentStep === number;
        return (
          <div key={title} className={`crm-baja-terminal__step ${active ? "is-active" : ""} ${completed ? "is-completed" : ""}`}>
            <span className="crm-baja-terminal__step-circle">{completed ? <FontAwesomeIcon icon={faCheck} /> : number}</span>
            <span className="crm-baja-terminal__step-copy">
              <strong>{title}</strong>
              <small>{subtitle}</small>
            </span>
          </div>
        );
      })}
    </div>
  );
}

function DeviceCard({ details, model }) {
  const terminal = read(details, "nDeTerminal", "NDeTerminal");
  const comercio = read(details, "nDeComercio", "NDeComercio");
  const nombre = read(details, "nombreDeFantasia", "NombreDeFantasia") || read(details, "razonSocial", "RazonSocial");
  const idClover = read(details, "idClover", "IdClover");
  const estado = read(details, "estado", "Estado");

  if (!details) {
    return (
      <aside className="crm-baja-terminal__device-card">
        <div className="crm-baja-terminal__device-head">
          <h3>Ficha tecnica del equipo</h3>
          <span className="crm-baja-terminal__badge is-muted">SIN IDENTIFICAR</span>
        </div>
        <div className="crm-baja-terminal__empty-device">
          <strong>Esperando una terminal</strong>
          <p>Ingresa el Nro. de terminal para mostrar el modelo, las fotografias y sus datos tecnicos.</p>
        </div>
      </aside>
    );
  }

  return (
    <aside className="crm-baja-terminal__device-card">
      <div className="crm-baja-terminal__device-head">
        <h3>Ficha tecnica del equipo</h3>
        <span className="crm-baja-terminal__badge">MODELO RECONOCIDO</span>
      </div>
      {model?.image ? (
        <div className="crm-baja-terminal__device-image">
          <img src={model.image} alt={`${model.model} ${model.generation}`} />
          <span>Foto del equipo</span>
        </div>
      ) : (
        <div className="crm-baja-terminal__device-image is-empty">Sin imagen disponible</div>
      )}
      <div className="crm-baja-terminal__device-model">
        <div><small>MARCA</small><strong>{model.brand}</strong></div>
        <div><small>MODELO</small><strong>{model.model}</strong></div>
        <div><small>GENERACION</small><strong>{model.generation}</strong></div>
      </div>
      <div className="crm-baja-terminal__technical-grid">
        <Info label="NRO. DE TERMINAL" value={terminal} />
        <Info label="NRO. DE COMERCIO" value={comercio} />
        <Info label="ID CLOVER" value={idClover ? `#${idClover}` : "-"} />
        <Info label="COMERCIO ASIGNADO" value={nombre} />
        <Info label="REGLA DETECTADA" value={model.rule} />
        <Info label="ESTADO" value={estado} />
      </div>
    </aside>
  );
}

function Info({ label, value }) {
  return (
    <div className="crm-baja-terminal__info">
      <small>{label}</small>
      <strong>{value || "-"}</strong>
    </div>
  );
}

export default function BajaTerminalFlow({ api, submitting = false, onSubmitBaja }) {
  const [state, setState] = useState(initialState);
  const [identifying, setIdentifying] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const model = useMemo(() => identifyDeviceModel(state.terminalDetails || {}), [state.terminalDetails]);
  const selectedCards = PROVINCIAL_CARDS.filter((card) => state.selectedProvincialCards[card.key]);
  const selectedCardsVerified = selectedCards.every((card) => state.verifiedProvincialCards[card.key]);

  const reset = async () => {
    const hasData = state.currentStep > 1 || state.terminalInput || state.observaciones;
    if (hasData) {
      const result = await Swal.fire({
        title: "Cancelar baja",
        text: "Se perderan los datos cargados en este proceso.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Cancelar proceso",
        cancelButtonText: "Volver",
        reverseButtons: true,
      });
      if (result && !result.isConfirmed) return;
    }
    setState(initialState());
  };

  const identifyTerminal = async (event) => {
    event?.preventDefault?.();
    const terminal = normalizeDigits(state.terminalInput);
    if (!terminal) {
      setState((prev) => ({ ...prev, error: "Ingresa un Nro. de terminal para continuar." }));
      return;
    }

    setIdentifying(true);
    try {
      const details = await api.obtenerTerminalDetalle(terminal);
      const faltantes = read(details, "datosFaltantes", "DatosFaltantes");
      if (Array.isArray(faltantes) && faltantes.length) {
        setState((prev) => ({ ...prev, terminalDetails: details, error: `La terminal fue encontrada pero faltan datos: ${faltantes.join(", ")}.` }));
        return;
      }
      const estado = String(read(details, "estado", "Estado")).trim().toUpperCase();
      if (estado && estado !== "ALTA") {
        setState((prev) => ({ ...prev, terminalDetails: details, error: `La terminal esta en estado ${estado}. No se puede avanzar con una baja nueva.` }));
        return;
      }
      setState((prev) => ({
        ...initialState(),
        terminalInput: terminal,
        terminalDetails: details,
        currentStep: 2,
      }));
    } catch (error) {
      setState((prev) => ({ ...prev, terminalDetails: null, error: error.message || "No se pudo identificar la terminal." }));
    } finally {
      setIdentifying(false);
    }
  };

  const stepValid = {
    1: Boolean(state.terminalDetails),
    2: Boolean(state.identityConfirmed),
    3:
      state.hasProvincialCards === "no"
        ? state.provincialReviewConfirmed
        : state.hasProvincialCards === "si" && selectedCards.length > 0 && selectedCardsVerified && state.provincialReviewConfirmed,
    4: Boolean(state.motivo && state.fechaBaja),
  };

  const buildTraceObservation = () => {
    const details = state.terminalDetails || {};
    const tarjetas = state.hasProvincialCards === "no" ? "No contiene" : selectedCards.map((card) => card.label).join(", ");
    const lines = [
      state.observaciones.trim(),
      "",
      "[Baja guiada por terminal]",
      `Terminal: ${read(details, "nDeTerminal", "NDeTerminal")}`,
      `Comercio: ${read(details, "nDeComercio", "NDeComercio")}`,
      `Comercio asignado: ${read(details, "nombreDeFantasia", "NombreDeFantasia") || read(details, "razonSocial", "RazonSocial") || "-"}`,
      `CUIT: ${read(details, "cuit", "Cuit") || "-"}`,
      `ID Clover: ${read(details, "idClover", "IdClover") || "-"}`,
      `Modelo identificado: ${model.brand} ${model.model} ${model.generation}`,
      `Regla utilizada: ${model.rule}`,
      "Identidad confirmada: Si",
      `Tarjetas provinciales: ${tarjetas}`,
      `Tarjetas verificadas: ${state.hasProvincialCards === "si" ? selectedCards.map((card) => card.label).join(", ") : "No aplica"}`,
      "Revision provincial confirmada: Si",
    ];
    return lines.filter((line, index) => index > 0 || line).join("\n");
  };

  const submit = async () => {
    if (!stepValid[4] || submitting) return;
    try {
      await onSubmitBaja({
        NumeroTerminalBaja: normalizeDigits(read(state.terminalDetails, "nDeTerminal", "NDeTerminal")),
        Motivo: state.motivo,
        Observacion: buildTraceObservation(),
        FechaBaja: asDateForApi(state.fechaBaja),
      });
      setShowSuccess(true);
    } catch (error) {
      setState((prev) => ({ ...prev, error: error.message || "No se pudo registrar la baja." }));
    }
  };

  const goNext = () => {
    if (stepValid[state.currentStep]) {
      setState((prev) => ({ ...prev, currentStep: Math.min(prev.currentStep + 1, 4), error: "" }));
    }
  };

  const setCardsAnswer = (value) => {
    setState((prev) => ({
      ...prev,
      hasProvincialCards: value,
      selectedProvincialCards: value === "no" ? {} : prev.selectedProvincialCards,
      verifiedProvincialCards: value === "no" ? {} : prev.verifiedProvincialCards,
      provincialReviewConfirmed: false,
    }));
  };

  const details = state.terminalDetails || {};
  const terminal = read(details, "nDeTerminal", "NDeTerminal");
  const comercio = read(details, "nDeComercio", "NDeComercio");
  const comercioNombre = read(details, "nombreDeFantasia", "NombreDeFantasia") || read(details, "razonSocial", "RazonSocial");
  const idClover = read(details, "idClover", "IdClover");
  const estado = read(details, "estado", "Estado");

  return (
    <div className="crm-baja-terminal">
      <div className="crm-baja-terminal__intro">
        <div>
          <span>CONTROL OPERATIVO DE TERMINALES</span>
          <h3>Baja guiada por terminal</h3>
          <p>Identifica el equipo, valida su vinculacion y confirma la baja de tarjetas provinciales antes de ejecutar el cierre.</p>
        </div>
        <div className="crm-baja-terminal__trace"><i />Proceso con trazabilidad</div>
      </div>

      <div className="crm-baja-terminal__layout">
        <section className="crm-baja-terminal__panel">
          <Stepper currentStep={state.currentStep} />
          {state.error ? <div className="crm-baja-terminal__error">{state.error}</div> : null}

          {state.currentStep === 1 ? (
            <form className="crm-baja-terminal__step-body" onSubmit={identifyTerminal}>
              <span className="crm-baja-terminal__badge">PASO 1 DE 4</span>
              <h3>Ingresa el numero del equipo</h3>
              <p>El CRM buscara la terminal y recuperara su Nro. de comercio, ID Clover y modelo asociado.</p>
              <Form.Group>
                <Form.Label>Nro. de terminal</Form.Label>
                <div className="crm-baja-terminal__terminal-row">
                  <Form.Control value={state.terminalInput} onChange={(e) => setState((prev) => ({ ...prev, terminalInput: e.target.value, error: "" }))} disabled={identifying} />
                  <button className="crm-altas-bajas-page__btn crm-altas-bajas-page__btn--primary" disabled={identifying} type="submit">
                    {identifying ? <><Spinner animation="border" size="sm" className="me-2" />Identificando...</> : <><FontAwesomeIcon icon={faMagnifyingGlass} className="me-2" />Identificar equipo</>}
                  </button>
                </div>
              </Form.Group>
            </form>
          ) : null}

          {state.currentStep === 2 ? (
            <div className="crm-baja-terminal__step-body">
              <span className="crm-baja-terminal__badge">PASO 2 DE 4</span>
              <h3>Verifica la identificacion</h3>
              <p>Compara los datos del CRM con el equipo fisico antes de avanzar.</p>
              <div className="crm-baja-terminal__readonly-grid">
                <Info label="Nro. de terminal" value={terminal} />
                <Info label="Nro. de comercio" value={comercio} />
                <Info label="Comercio" value={comercioNombre} />
                <Info label="ID Clover" value={idClover || "-"} />
                <Info label="Modelo detectado" value={`${model.model} - ${model.generation}`} />
                <Info label="Estado actual" value={estado} />
              </div>
              <label className="crm-baja-terminal__check">
                <input type="checkbox" checked={state.identityConfirmed} onChange={(e) => setState((prev) => ({ ...prev, identityConfirmed: e.target.checked }))} />
                <span>Confirmo que el Nro. de terminal, el Nro. de comercio y el equipo mostrado corresponden a la terminal que se dara de baja.</span>
              </label>
              <div className="crm-baja-terminal__notice">Si algun dato no coincide, detene el proceso y corregi la vinculacion antes de generar la baja.</div>
            </div>
          ) : null}

          {state.currentStep === 3 ? (
            <div className="crm-baja-terminal__step-body">
              <span className="crm-baja-terminal__badge">PASO 3 DE 4</span>
              <h3>Revisa las tarjetas provinciales</h3>
              <p>La validacion debe realizarse en Clover o directamente en el equipo.</p>
              <div className="crm-baja-terminal__choice-grid">
                <button type="button" className={state.hasProvincialCards === "no" ? "is-selected" : ""} onClick={() => setCardsAnswer("no")}>
                  <strong>NO</strong><span>El equipo no tiene tarjetas provinciales.</span>
                </button>
                <button type="button" className={state.hasProvincialCards === "si" ? "is-selected" : ""} onClick={() => setCardsAnswer("si")}>
                  <strong>SI</strong><span>Seleccionar y verificar cada tarjeta.</span>
                </button>
              </div>
              {state.hasProvincialCards === "si" ? (
                <div className="crm-baja-terminal__cards-review">
                  <h4>Selecciona cuales contiene</h4>
                  {PROVINCIAL_CARDS.map((card) => {
                    const selected = Boolean(state.selectedProvincialCards[card.key]);
                    return (
                      <div key={card.key} className="crm-baja-terminal__card-row">
                        <label><input type="checkbox" checked={selected} onChange={(e) => setState((prev) => ({ ...prev, selectedProvincialCards: { ...prev.selectedProvincialCards, [card.key]: e.target.checked }, verifiedProvincialCards: { ...prev.verifiedProvincialCards, [card.key]: e.target.checked ? prev.verifiedProvincialCards[card.key] : false } }))} />{card.label}</label>
                        <label><input type="checkbox" disabled={!selected} checked={Boolean(state.verifiedProvincialCards[card.key])} onChange={(e) => setState((prev) => ({ ...prev, verifiedProvincialCards: { ...prev.verifiedProvincialCards, [card.key]: e.target.checked } }))} />Baja verificada</label>
                      </div>
                    );
                  })}
                </div>
              ) : null}
              {state.hasProvincialCards ? (
                <label className="crm-baja-terminal__check">
                  <input type="checkbox" checked={state.provincialReviewConfirmed} onChange={(e) => setState((prev) => ({ ...prev, provincialReviewConfirmed: e.target.checked }))} />
                  <span>{state.hasProvincialCards === "no" ? "Confirmo que revise Clover o el equipo y que no contiene tarjetas provinciales activas." : "Confirmo que revise Clover o el equipo y que las tarjetas seleccionadas fueron dadas de baja correctamente."}</span>
                </label>
              ) : null}
            </div>
          ) : null}

          {state.currentStep === 4 ? (
            <div className="crm-baja-terminal__step-body">
              <span className="crm-baja-terminal__badge">PASO 4 DE 4</span>
              <h3>Confirma y carga la baja</h3>
              <p>Revisa el resumen final. El sistema conservara la fecha, hora, usuario y validaciones realizadas.</p>
              <div className="crm-baja-terminal__summary">
                <Info label="TERMINAL" value={terminal} />
                <Info label="COMERCIO" value={comercio} />
                <Info label="EQUIPO" value={`${model.model} - ${model.generation}`} />
                <Info label="TARJETAS" value={state.hasProvincialCards === "no" ? "No contiene" : selectedCards.map((card) => card.label).join(", ")} />
              </div>
              <div className="crm-baja-terminal__final-form">
                <Form.Group>
                  <Form.Label>Motivo *</Form.Label>
                  <Form.Select value={state.motivo} onChange={(e) => setState((prev) => ({ ...prev, motivo: e.target.value }))}>
                    {BAJA_MOTIVOS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Fecha de baja *</Form.Label>
                  <Form.Control type="date" value={state.fechaBaja} onChange={(e) => setState((prev) => ({ ...prev, fechaBaja: e.target.value }))} />
                </Form.Group>
                <Form.Group className="crm-baja-terminal__span-2">
                  <Form.Label>Observaciones</Form.Label>
                  <Form.Control as="textarea" rows={4} value={state.observaciones} onChange={(e) => setState((prev) => ({ ...prev, observaciones: e.target.value }))} />
                </Form.Group>
              </div>
            </div>
          ) : null}

          <footer className="crm-baja-terminal__footer">
            <button type="button" className="crm-altas-bajas-page__btn crm-altas-bajas-page__btn--secondary" onClick={reset}><FontAwesomeIcon icon={faRotateLeft} className="me-2" />Cancelar</button>
            <div>
              <button type="button" className="crm-altas-bajas-page__btn crm-altas-bajas-page__btn--secondary" disabled={state.currentStep === 1 || submitting} onClick={() => setState((prev) => ({ ...prev, currentStep: Math.max(prev.currentStep - 1, 1), error: "" }))}>Anterior</button>
              {state.currentStep < 4 ? (
                <button type="button" className="crm-altas-bajas-page__btn crm-altas-bajas-page__btn--primary" disabled={!stepValid[state.currentStep]} onClick={goNext}>Continuar</button>
              ) : (
                <button type="button" className="crm-altas-bajas-page__btn crm-altas-bajas-page__btn--primary" disabled={!stepValid[4] || submitting} onClick={submit}>{submitting ? "Procesando..." : "Confirmar y cargar baja"}</button>
              )}
            </div>
          </footer>
        </section>
        <DeviceCard details={state.terminalDetails} model={model} />
      </div>

      <Modal show={showSuccess} centered onHide={() => { setShowSuccess(false); setState(initialState()); }} className="crm-baja-terminal__success-modal">
        <Modal.Body>
          <FontAwesomeIcon icon={faCircleCheck} />
          <h3>Baja cargada correctamente</h3>
          <p>La terminal {terminal} fue registrada con todas sus validaciones.</p>
          <button type="button" className="crm-altas-bajas-page__btn crm-altas-bajas-page__btn--primary" onClick={() => { setShowSuccess(false); setState(initialState()); }}>Finalizar</button>
        </Modal.Body>
      </Modal>
    </div>
  );
}
